import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

/**
 * A general-purpose dropdown menu component built with Radix UI
 *
 * @param {Object} props
 * @param {React.ReactNode} props.trigger - The element that triggers the dropdown
 * @param {React.ReactNode} props.children - The dropdown content
 * @param {string} [props.align="end"] - Alignment of the dropdown (start, center, end)
 * @param {string} [props.side="bottom"] - Side of the trigger to show the dropdown (top, right, bottom, left)
 * @param {number} [props.sideOffset=5] - Distance between trigger and dropdown
 * @param {boolean} [props.open] - Controlled open state
 * @param {Function} [props.onOpenChange] - Function called when open state changes
 */
const Dropdown = ({
  trigger,
  children,
  align = "end",
  side = "bottom",
  sideOffset = 5,
  open,
  onOpenChange,
}) => {
  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          style={{
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.01)",
          }}
          className="bg-white border-neutral-200 border min-w-[220px] p-1 z-50 will-change-[opacity,transform] data-[side=top]:animate-slideUpAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideRightAndFade"
          sideOffset={sideOffset}
          align={align}
          side={side}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

// Sub-components for easy usage
Dropdown.Item = function Item({ children, onSelect, className, ...props }) {
  return (
    <DropdownMenu.Item
      className={`text-sm leading-none text-penta-black flex items-center h-9 px-3 relative select-none outline-none cursor-pointer hover:bg-penta-grey-light transition-colors duration-200 ${
        className || ""
      }`}
      onSelect={onSelect}
      {...props}
    >
      {children}
    </DropdownMenu.Item>
  );
};

Dropdown.Separator = function Separator() {
  return <DropdownMenu.Separator className="h-px bg-neutral-200 my-1" />;
};

Dropdown.Label = function Label({ children, className, ...props }) {
  return (
    <DropdownMenu.Label
      className={`pl-3 py-2 text-xs font-mono text-penta-grey-medium uppercase tracking-wide ${className || ""}`}
      {...props}
    >
      {children}
    </DropdownMenu.Label>
  );
};

export default Dropdown;
